import type { SrpTopEmployersStripItem } from '../../types/srp-top-employers-strip';
import type { FacetsData } from '../facets/facets-shared';
import { isStaffingAgency } from '../company/is-staffing-agency';

const MIN_DIRECT_EMPLOYERS = 6;

function displayNameFromHit(hit: Record<string, unknown>): string {
  const company = hit.company;
  if (Array.isArray(company) && company.length > 0) {
    const first = company[0];
    if (typeof first === 'string' && first.trim()) return first.trim();
  }
  if (typeof company === 'string' && company.trim()) return company.trim();
  const cid = String(hit.companyId ?? '').trim();
  return cid;
}

/**
 * When the companies-index query returns nothing, derive top employers for the SRP strip from the
 * same job search response (companyId facet + hit labels) so SSR still includes real links/names.
 */
export function buildTopEmployersStripFallbackFromSearch(
  facetsData: FacetsData | undefined,
  hits: Array<Record<string, unknown>> | undefined,
  limit: number,
  options: {
    /**
     * Skip staffing agencies, so a hub's strip links the employers that actually hire there
     * rather than the same handful of agencies on every page; agencies only top the list up
     * when fewer than MIN_DIRECT_EMPLOYERS direct employers remain.
     */
    excludeAgencies?: boolean;
  } = {},
): SrpTopEmployersStripItem[] {
  if (!Number.isFinite(limit) || limit <= 0) return [];
  const companyFacets = facetsData?.companyId;
  if (!Array.isArray(companyFacets) || companyFacets.length === 0) return [];

  const nameById = new Map<string, string>();
  for (const hit of hits ?? []) {
    const id = String(hit?.companyId ?? '').trim();
    if (!id || nameById.has(id)) continue;
    const name = displayNameFromHit(hit);
    nameById.set(id, name.trim() ? name : id);
  }

  const direct: SrpTopEmployersStripItem[] = [];
  const agencies: SrpTopEmployersStripItem[] = [];
  for (const item of companyFacets) {
    const value = typeof item.value === 'string' ? item.value.trim() : '';
    if (!value) continue;
    const count = typeof item.count === 'number' ? item.count : Number(item.count);
    if (!Number.isFinite(count) || count <= 0) continue;
    const entry = { companyId: value, name: nameById.get(value) ?? value, logoUrl: null };
    if (options.excludeAgencies && isStaffingAgency(value, entry.name)) agencies.push(entry);
    else direct.push(entry);
    if (direct.length >= limit) break;
  }
  if (!options.excludeAgencies || direct.length >= MIN_DIRECT_EMPLOYERS) return direct.slice(0, limit);
  return [...direct, ...agencies].slice(0, limit);
}
